/**
 * 브라우저에 쌓이는 데이터를 계정별로 갈라 놓는다.
 *
 * 키워드 수집·분석, 카테고리 소싱, 경쟁분석 결과는 IndexedDB 에만 있다.
 * 서버에 사본이 없는 유일본이라 지울 수 없고, 그렇다고 이름이 하나면
 * 계정을 바꿔도 앞 계정 데이터가 그대로 보인다.
 * 그래서 지우는 대신 DB 이름 뒤에 계정을 붙여 서랍을 나눈다.
 *
 *   KeywordCollectDB  →  KeywordCollectDB__<계정_id>
 *
 * 이미 쓰던 사람의 데이터(이름 없는 옛 DB)는 처음 한 번 복사해 옮긴다.
 * 옛 DB 는 지우지 않는다 — 복사가 잘못돼도 원본이 남아 있어야 한다.
 * 옛 데이터가 누구 것인지는 알 수 없으므로 '업데이트 후 처음 로그인한 계정'이
 * 가져가고, 그 사실을 남겨 다른 계정이 또 가져가지 않게 한다.
 */

const SEP = '__';
const CLAIM_KEY = 'couplus.dbScope.claimedBy';

// 계정별로 갈라야 하는 DB. 값이 그 계정 것일 때만 넣는다.
// CouplusLabels(담당자 이니셜)는 계정이 아니라 사람의 것이라 넣지 않았다.
// couplus-ledger 는 이미 레코드 키에 계정이 들어가 있어 여기서 다루지 않는다.
export const SCOPED_DB_NAMES = [
    'KeywordCollectDB',
    'KeywordAnalysisDB',
    'CategorySourcingDB',
    'categorySourcingGroups',
    'productCompetitiveAnalysis',
    'ProductLinkRankCompareDB',
    'NanoBananaDB',
    'couplustDB'
];

let currentScope = '';

// indexedDB.databases() 가 없으면 옛 DB 가 있는지 확인할 방법이 없다.
// 그 브라우저에서는 이름을 나누지 않는다 — 나누는 순간 옛 데이터가 안 보이게 된다.
function canEnumerate() {
    return typeof indexedDB !== 'undefined' && typeof indexedDB.databases === 'function';
}

/** 로그인 정보가 확정된 뒤 한 번 호출한다 (App.vue) */
export function setDbScope(userId) {
    currentScope = userId || '';
}

/** 화면에서 indexedDB.open 에 넘길 이름 */
export function scopedDbName(baseName) {
    if (!currentScope || !canEnumerate()) return baseName;
    return baseName + SEP + currentScope;
}

function toPromise(req) {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

// 버전을 지정하지 않고 연다 — 이미 있는 DB 를 있는 그대로 열기 위해서다
function openExisting(name) {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(name);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        req.onblocked = () => reject(new Error('DB 가 다른 탭에서 열려 있습니다'));
    });
}

/** 옛 DB 를 스키마째 새 이름으로 복사한다 (원본은 그대로 둔다) */
async function copyDatabase(fromName, toName) {
    const src = await openExisting(fromName);
    const version = src.version;
    const stores = [];

    try {
        for (const storeName of Array.from(src.objectStoreNames)) {
            const os = src.transaction(storeName, 'readonly').objectStore(storeName);
            const indexes = Array.from(os.indexNames).map(n => {
                const ix = os.index(n);
                return { name: n, keyPath: ix.keyPath, unique: ix.unique, multiEntry: ix.multiEntry };
            });
            stores.push({
                name: storeName,
                keyPath: os.keyPath,
                autoIncrement: os.autoIncrement,
                indexes,
                records: await toPromise(os.getAll()),
                // keyPath 가 없는 스토어는 키를 따로 들고 가야 한다
                keys: os.keyPath === null ? await toPromise(os.getAllKeys()) : null
            });
        }
    } finally {
        src.close();
    }

    const dst = await new Promise((resolve, reject) => {
        const req = indexedDB.open(toName, version);
        req.onupgradeneeded = () => {
            const db = req.result;
            stores.forEach(s => {
                const os = db.createObjectStore(s.name, { keyPath: s.keyPath, autoIncrement: s.autoIncrement });
                s.indexes.forEach(ix => {
                    os.createIndex(ix.name, ix.keyPath, { unique: ix.unique, multiEntry: ix.multiEntry });
                });
            });
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });

    try {
        for (const s of stores) {
            if (!s.records.length) continue;
            await new Promise((resolve, reject) => {
                const tx = dst.transaction(s.name, 'readwrite');
                const os = tx.objectStore(s.name);
                s.records.forEach((r, i) => { s.keys ? os.put(r, s.keys[i]) : os.put(r); });
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            });
        }
    } finally {
        dst.close();
    }

    const total = stores.reduce((n, s) => n + s.records.length, 0);
    console.log('[scopedDB]', fromName, '→', toName, total + '건 이관');
}

/**
 * 로그인 직후 한 번 실행한다. 실패해도 화면은 계속 떠야 하므로 던지지 않는다.
 * @param {string} userId 로그인한 계정 _id
 */
export async function migrateLegacyDbs(userId) {
    if (!userId || !canEnumerate()) return;

    let claimed;
    try {
        claimed = localStorage.getItem(CLAIM_KEY);
    } catch (e) {
        console.warn('[scopedDB] 이관 기록을 읽지 못했습니다', e.message);
        return;
    }
    // 옛 데이터는 한 계정만 가져간다. 다른 계정이 이미 가져갔으면 건드리지 않는다
    if (claimed && claimed !== userId) return;

    let existing;
    try {
        existing = new Set((await indexedDB.databases()).map(d => d.name));
    } catch (e) {
        console.warn('[scopedDB] DB 목록을 읽지 못했습니다', e.message);
        return;
    }

    for (const base of SCOPED_DB_NAMES) {
        const scoped = base + SEP + userId;
        if (!existing.has(base)) continue;      // 옮길 옛 데이터가 없다
        if (existing.has(scoped)) continue;     // 이미 옮겼다
        try {
            await copyDatabase(base, scoped);
        } catch (e) {
            // 한 DB 가 실패해도 나머지는 옮긴다. 원본은 그대로 있다
            console.error('[scopedDB] 이관 실패 :', base, e.message);
        }
    }

    try {
        localStorage.setItem(CLAIM_KEY, userId);
    } catch (e) {
        console.warn('[scopedDB] 이관 기록을 남기지 못했습니다', e.message);
    }
}

export default { setDbScope, scopedDbName, migrateLegacyDbs, SCOPED_DB_NAMES };
